import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const ROLE_COLORS: Record<string, string> = {
  free: '#16a34a',
  premium: '#d97706',
  admin: '#dc2626',
};

const VALID_ROLES = ['free', 'premium', 'admin'];

interface AppUser {
  id: string;
  email: string;
  role: string;
  created_at: string;
  last_active: string | null;
  is_admin: boolean;
}

function daysSince(dateStr: string | null): string {
  if (!dateStr) return 'necunoscut';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diff === 0) return 'azi';
  if (diff === 1) return 'ieri';
  return `acum ${diff} zile`;
}

function UserCard({ user, onUpdate }: { user: AppUser; onUpdate: (u: AppUser) => void }) {
  const [expanded, setExpanded] = useState(false);

  const changeRole = async (role: string) => {
    try {
      await api.patch(`/admin/users-list/${user.id}/role`, { role });
      onUpdate({ ...user, role });
    } catch {}
  };

  const roleColor = ROLE_COLORS[user.role] || '#6b7280';
  const dateFormatted = user.created_at
    ? new Date(user.created_at).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10, background: '#fff', overflow: 'hidden' }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: roleColor, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 15, flexShrink: 0,
        }}>
          {user.email[0].toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.email}
            </div>
            {user.is_admin && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>
                Admin
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>
            {dateFormatted} · Activ {daysSince(user.last_active)}
          </div>
        </div>

        <span style={{
          padding: '2px 10px', borderRadius: 20,
          background: `${roleColor}20`, color: roleColor,
          fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}>
          {user.role}
        </span>

        <span style={{ color: '#9ca3af', fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #f3f4f6', padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, marginBottom: 8 }}>ROL</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {VALID_ROLES.map(r => (
              <button
                key={r}
                onClick={() => changeRole(r)}
                style={{
                  padding: '4px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontWeight: 600,
                  background: user.role === r ? `${ROLE_COLORS[r]}20` : '#f9fafb',
                  color: user.role === r ? ROLE_COLORS[r] : '#6b7280',
                  border: user.role === r ? `1.5px solid ${ROLE_COLORS[r]}` : '1.5px solid #e5e7eb',
                }}
              >{r}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users-list');
      setUsers(res.data);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const updateUser = (updated: AppUser) =>
    setUsers(prev => prev.map(u => (u.id === updated.id ? updated : u)));

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const roleCounts = VALID_ROLES.reduce<Record<string, number>>((acc, r) => {
    acc[r] = users.filter(u => u.role === r).length;
    return acc;
  }, {});

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Utilizatori · Flexara</h1>
          <span style={{ fontSize: 14, color: '#6b7280' }}>{users.length} utilizatori</span>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
          {VALID_ROLES.map(r => (
            <div key={r} style={{
              padding: '10px 16px', borderRadius: 10, background: '#fff',
              border: `1.5px solid ${ROLE_COLORS[r]}30`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80,
            }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: ROLE_COLORS[r] }}>{roleCounts[r]}</span>
              <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{r}</span>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Caută după email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 8,
              border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', background: '#fff',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Se încarcă...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Niciun utilizator găsit.</div>
        ) : (
          filtered.map(u => <UserCard key={u.id} user={u} onUpdate={updateUser} />)
        )}
      </div>
    </div>
  );
}
